/**
 * EditableText Component
 *
 * Inline text editing with double-click to edit, auto-save on blur.
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import clsx from 'clsx';

export interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
}

export function EditableText({
  value,
  onChange,
  className,
  inputClassName,
  placeholder,
  disabled = false,
  multiline = false,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [inputWidth, setInputWidth] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const originalValueRef = useRef(value);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const measureWidth = useCallback(
    (text: string): number => {
      if (!measureRef.current || !wrapperRef.current) return 0;
      const wrapperStyles = window.getComputedStyle(wrapperRef.current);
      measureRef.current.style.fontSize = wrapperStyles.fontSize;
      measureRef.current.style.fontFamily = wrapperStyles.fontFamily;
      measureRef.current.style.fontWeight = wrapperStyles.fontWeight;
      measureRef.current.style.letterSpacing = wrapperStyles.letterSpacing;
      measureRef.current.style.padding = wrapperStyles.padding;
      measureRef.current.textContent = text || placeholder || 'M';
      return measureRef.current.offsetWidth;
    },
    [placeholder]
  );

  const handleStartEditing = useCallback(() => {
    originalValueRef.current = value;
    if (wrapperRef.current) {
      const width = measureWidth(value || placeholder || '');
      setInputWidth(Math.max(width, 50));
    }
    setIsEditing(true);
  }, [value, placeholder, measureWidth]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setEditValue(newValue);

      if (!multiline && inputRef.current instanceof HTMLInputElement) {
        const newWidth = measureWidth(newValue || placeholder || '');
        setInputWidth(Math.max(newWidth, inputWidth));
      }
    },
    [inputWidth, placeholder, measureWidth, multiline]
  );

  const handleBlur = () => {
    const trimmed = editValue.trim();
    if (trimmed !== value) {
      onChange(trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      const trimmed = editValue.trim();
      onChange(trimmed);
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      setEditValue(originalValueRef.current);
      setIsEditing(false);
    } else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const trimmed = editValue.trim();
      onChange(trimmed);
      setIsEditing(false);
    }
  };

  if (disabled) {
    return (
      <span className={className} title={value}>
        {value || placeholder}
      </span>
    );
  }

  const InputComponent = multiline ? 'textarea' : 'input';
  const displayText = value || placeholder;

  return (
    <span
      ref={wrapperRef}
      className={clsx(
        className,
        !isEditing && 'cursor-text hover:text-accent-violet transition-colors'
      )}
      onClick={(e) => {
        // Use click event with detail check as primary handler
        // onClick with detail === 2 is more reliable than onDoubleClick in some browsers/environments
        if (!isEditing && e.detail >= 2) {
          e.stopPropagation();
          e.preventDefault();
          handleStartEditing();
        }
      }}
      onDoubleClick={(e) => {
        // Keep onDoubleClick as fallback
        if (!isEditing) {
          e.stopPropagation();
          e.preventDefault();
          handleStartEditing();
        }
      }}
      title={isEditing ? undefined : 'Doppio click per modificare'}
      style={{
        display: 'inherit',
        position: 'relative',
        minWidth: isEditing ? `${inputWidth}px` : undefined,
      }}
    >
      <span
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          top: '-9999px',
          left: '-9999px',
          pointerEvents: 'none',
        }}
      />

      {isEditing ? (
        <InputComponent
          ref={inputRef as React.Ref<HTMLInputElement & HTMLTextAreaElement>}
          type="text"
          value={editValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={clsx(
            inputClassName || className,
            'm-0 p-0 border-0 outline-none bg-transparent',
            'resize-none'
          )}
          placeholder={placeholder}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          style={{
            width: `${inputWidth}px`,
            minWidth: '50px',
            height: multiline ? '60px' : '1em',
            lineHeight: 'inherit',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            verticalAlign: 'baseline',
            boxSizing: 'border-box',
          }}
        />
      ) : (
        <span>{displayText}</span>
      )}
    </span>
  );
}
